import { IPlusUsageData, IParapassQuizData, IParapassUsageData, IParapassBooksProgress } from "@interfaces/Interfaces";
import { format } from "date-fns";
import { capitalizeFirstLetter, escapeCSV } from "@utils/stringUtils";
import DataController from "@controllers/DataController";
import _ from "lodash";
import { TimelineData } from "@interfaces/TimelineTypes";

function allTimeLineDataIsPlusData(searchResults: TimelineData[]): boolean {
  return searchResults.every((item) => "GLID" in item);
}

function allTimeLineDataIsParapassQuizData(searchResults: TimelineData[]): boolean {
  return searchResults.every((item) => "quizSet" in item);
}

function allTimeLineDataIsParapassUsageData(searchResults: TimelineData[]): boolean {
  return searchResults.every((item) => "ContentType" in item);
}

function allTimeLineDataIsParapassBooksProgressData(searchResults: TimelineData[]): boolean {
  return searchResults.every((item) => isParapassBooksProgress(item));
}

const PLUS_TIMELINE_CSV_ROWS = ["Title", "Date", "Type", "Activity Description", "App"];

const PARAPASS_TIMELINE_CSV_ROWS = ["Title", "Date", "Type", "Activity Description", "App"];

const PARAPASS_USAGE_TIMELINE_CSV_ROWS = ["Title", "Date", "Type", "Activity Description", "App"];

const PARAPASS_BOOKS_PROGRESS_TIMELINE_CSV_ROWS = ["Title", "Date", "Type", "Activity Description", "App"];

const TIMELINE_CSV_ROWS = ["Title", "Date", "Type", "Activity Description", "App"];

function getPlusTimelineCSVMappedValue(item: IPlusUsageData, row: string): string {
  switch (row) {
    case "Date":
      return format(new Date(item.timestamp), "dd-MM-yyyy");
    case "GLID":
      return escapeCSV(item.GLID);
    case "Title":
      return escapeCSV(item.title);
    case "Activity Description": {
      const count = item.count ?? 1;
      const activityDescription = `Viewed JRCALC ${DataController.getJRCALCGuidelineType(item.GLID)} “${item.title}” ${count ? `${count} time${count === 1 ? "" : "s"}` : ""} ${format(item.sortableDate!, "dd MMM yyyy")}`;
      return escapeCSV(activityDescription);
    }
    case "Type":
      return escapeCSV(capitalizeFirstLetter(DataController.getJRCALCGuidelineType(item.GLID)));
    case "App":
      return escapeCSV("JRCALC Plus");
    default:
      return "";
  }
}

function getParapassTimelineCSVMappedValue(item: IParapassQuizData, row: string): string {
  switch (row) {
    case "Date":
      return format(new Date(item.date), "dd-MM-yyyy");
    case "Title":
      return escapeCSV(item.title);
    case "Activity Description": {
      const totalScore = item.score.correct + item.score.incorrect + item.score.unanswered;
      const activityDescription = `Completed ParaPass “${item.title}” quiz and scored ${item.score.correct}/${totalScore} on ${format(item.sortableDate!, "dd MMM yyyy")}`;
      return escapeCSV(activityDescription);
    }
    case "Type":
      return escapeCSV(capitalizeFirstLetter(DataController.getParapassQuizType(item.type)));
    case "App":
      return escapeCSV("ParaPass");
    default:
      return "";
  }
}

function getParapassUsageTimelineCSVMappedValue(item: IParapassUsageData, row: string): string {
  switch (row) {
    case "Date":
      return format(new Date(item.TimeStamp), "dd-MM-yyyy");
    case "Title":
      return escapeCSV(item.title);
    case "Activity Description": {
      const activityDescription = `Viewed ParaPass ${item.ContentType} “${item.title}” on ${format(item.sortableDate!, "dd MMM yyyy")}`;
      return escapeCSV(activityDescription);
    }
    case "Type":
      return escapeCSV(capitalizeFirstLetter(item.ContentType));
    case "App":
      return escapeCSV("ParaPass");
    default:
      return "";
  }
}

function getParapassBooksProgressTimelineCSVMappedValue(item: IParapassBooksProgress, row: string): string {
  switch (row) {
    case "Date":
      return format(new Date(item.sortableDate!), "dd-MM-yyyy");
    case "Title":
      return escapeCSV(item.title);
    case "Activity Description": {
      let activityDescription;

      if (typeof item.ChapterNumber === "number") {
        activityDescription = `Read ParaPass Book “${item.title}” Chapter ${item.ChapterNumber} - “${item.ChapterName}” on ${format(item.sortableDate!, "dd MMM yyyy")}`;
      } else {
        activityDescription = `Read ParaPass Book “${item.title}” on ${format(item.sortableDate!, "dd MMM yyyy")}`;
      }

      return escapeCSV(activityDescription);
    }
    case "Type":
      return escapeCSV(mapParapassBookFilter(item.filterType));
    case "App":
      return escapeCSV("ParaPass");
    default:
      return "";
  }
}

function getTimelineCSVMappedValue(item: TimelineData, row: string): string {
  if ("GLID" in item) {
    return getPlusTimelineCSVMappedValue(item as IPlusUsageData, row);
  } else if ("ContentType" in item) {
    return getParapassUsageTimelineCSVMappedValue(item as IParapassUsageData, row);
  } else if (isParapassBooksProgress(item)) {
    return getParapassBooksProgressTimelineCSVMappedValue(item as IParapassBooksProgress, row);
  } else if ("quizSet" in item) {
    return getParapassTimelineCSVMappedValue(item as IParapassQuizData, row);
  }

  return "";
}

function generatePlusTimelineCSV(plusData: IPlusUsageData[]): string {
  let csv = "";

  csv += PLUS_TIMELINE_CSV_ROWS.join(",") + "\n";

  for (const item of plusData) {
    for (let i = 0; i < PLUS_TIMELINE_CSV_ROWS.length; i++) {
      const row = PLUS_TIMELINE_CSV_ROWS[i];

      csv += getPlusTimelineCSVMappedValue(item, row);

      if (i < PLUS_TIMELINE_CSV_ROWS.length - 1) {
        csv += ",";
      }
    }

    csv += "\n";
  }

  return csv;
}

function generateParapassQuizTimelineCSV(parapassData: IParapassQuizData[]): string {
  let csv = "";
  csv += PARAPASS_TIMELINE_CSV_ROWS.join(",") + "\n";

  for (const item of parapassData) {
    for (let i = 0; i < PARAPASS_TIMELINE_CSV_ROWS.length; i++) {
      const row = PARAPASS_TIMELINE_CSV_ROWS[i];

      csv += getParapassTimelineCSVMappedValue(item, row);

      if (i < PARAPASS_TIMELINE_CSV_ROWS.length - 1) {
        csv += ",";
      }
    }

    csv += "\n";
  }

  return csv;
}

function generateParapassUsageTimelineCSV(parapassUsageData: IParapassUsageData[]): string {
  let csv = "";
  csv += PARAPASS_USAGE_TIMELINE_CSV_ROWS.join(",") + "\n";

  for (const item of parapassUsageData) {
    for (let i = 0; i < PARAPASS_USAGE_TIMELINE_CSV_ROWS.length; i++) {
      const row = PARAPASS_USAGE_TIMELINE_CSV_ROWS[i];

      csv += getParapassUsageTimelineCSVMappedValue(item, row);

      if (i < PARAPASS_USAGE_TIMELINE_CSV_ROWS.length - 1) {
        csv += ",";
      }
    }

    csv += "\n";
  }

  return csv;
}

function generateParapassBooksProgressTimelineCSV(parapassBooksProgress: IParapassBooksProgress[]): string {
  let csv = "";
  csv += PARAPASS_BOOKS_PROGRESS_TIMELINE_CSV_ROWS.join(",") + "\n";

  for (const item of parapassBooksProgress) {
    for (let i = 0; i < PARAPASS_BOOKS_PROGRESS_TIMELINE_CSV_ROWS.length; i++) {
      const row = PARAPASS_BOOKS_PROGRESS_TIMELINE_CSV_ROWS[i];

      csv += getParapassBooksProgressTimelineCSVMappedValue(item, row);

      if (i < PARAPASS_BOOKS_PROGRESS_TIMELINE_CSV_ROWS.length - 1) {
        csv += ",";
      }
    }

    csv += "\n";
  }

  return csv;
}

function generateTimelineCSV(searchResults: TimelineData[]): string {
  let csv = "";

  csv += TIMELINE_CSV_ROWS.join(",") + "\n";

  const clonedSearchResults = structuredClone(searchResults);
  const sortedSearchResults = _.orderBy(clonedSearchResults, ["sortableDate"], ["desc"]);

  for (const item of sortedSearchResults) {
    for (let i = 0; i < TIMELINE_CSV_ROWS.length; i++) {
      const row = TIMELINE_CSV_ROWS[i];

      csv += getTimelineCSVMappedValue(item, row);

      if (i < TIMELINE_CSV_ROWS.length - 1) {
        csv += ",";
      }
    }

    csv += "\n";
  }

  return csv;
}

export function generateCSV(searchResults: TimelineData[]): string {
  if (allTimeLineDataIsPlusData(searchResults)) {
    return generatePlusTimelineCSV(searchResults as IPlusUsageData[]);
  } else if (allTimeLineDataIsParapassQuizData(searchResults)) {
    return generateParapassQuizTimelineCSV(searchResults as IParapassQuizData[]);
  } else if (allTimeLineDataIsParapassUsageData(searchResults)) {
    return generateParapassUsageTimelineCSV(searchResults as IParapassUsageData[]);
  } else if (allTimeLineDataIsParapassBooksProgressData(searchResults)) {
    return generateParapassBooksProgressTimelineCSV(searchResults as IParapassBooksProgress[]);
  }

  return generateTimelineCSV(searchResults);
}

export function mapParapassBookFilter(filterType: string | undefined): string {
  switch (filterType) {
    case "book":
      return "Book";
    case "chapter":
      return "Book Chapter";
    default:
      return filterType ?? "";
  }
}

export function getParapassFilters(params: {
  parapassQuizData: IParapassQuizData[];
  parapassUsageData: IParapassUsageData[];
  parapassBooksProgress: IParapassBooksProgress[];
}): string[] {
  const { parapassQuizData, parapassUsageData, parapassBooksProgress } = params;

  const quizFilters = _.uniq(parapassQuizData.map((item) => item.filterType));
  const usageFilters = _.uniq(parapassUsageData.map((item) => item.filterType));
  const booksProgressFilters = _.uniq(parapassBooksProgress.map((item) => mapParapassBookFilter(item.filterType)));

  const array = _.uniq([...quizFilters, ...usageFilters, ...booksProgressFilters]);
  const filters = array.filter((item) => item !== undefined) as string[];

  return filters.sort();
}

export function fetPlusFilters(params: { plusData: IPlusUsageData[] }): string[] {
  const { plusData } = params;

  const plusFilters = _.uniq(plusData.map((item) => item.filterType));

  const array = _.uniq([...plusFilters]);

  const filters = array.filter((item) => item !== undefined) as string[];

  return filters.sort();
}

export function isParapassBooksProgress(item: TimelineData): boolean {
  const bookTypes = ["book", "chapter"];

  return "type" in item && bookTypes.includes(item.type);
}
